import { Formik, Form } from "formik";
import { useConfirm } from "material-ui-confirm";
import { useState } from "react";
import { Link } from "react-router-dom";
import * as yup from "yup";

import {
  Card,
  CardHeader,
  CardContent,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  MenuItem,
  makeStyles
} from "@material-ui/core";

import { useTenant } from "src/components/LoggedInAdminProvider";
import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import { FormikSelect } from "src/components/form/Select";
import {
  JobStatus,
  TenantExportS3SettingsCreateInput,
  TenantExportS3SettingsUpdateInput
} from "src/graphql/generated/globalTypes";
import { listTenantExportConfigs_listTenantExportConfigs } from "src/graphql/generated/listTenantExportConfigs";
import {
  tenantExportConfigCreateS3,
  tenantExportConfigCreateS3Variables
} from "src/graphql/generated/tenantExportConfigCreateS3";
import {
  tenantExportConfigDelete,
  tenantExportConfigDeleteVariables
} from "src/graphql/generated/tenantExportConfigDelete";
import {
  tenantExportConfigUpdateS3,
  tenantExportConfigUpdateS3Variables
} from "src/graphql/generated/tenantExportConfigUpdateS3";
import {
  CREATE_S3_TENANT_EXPORT_CONFIG,
  DELETE_TENANT_EXPORT_CONFIG,
  UPDATE_S3_TENANT_EXPORT_CONFIG
} from "src/graphql/tenantExportConfigs";
import { getErrorMessage, graphqlErrorHasCode } from "src/util/errors";
import useMutation from "src/util/useMutation";

type ExportConfig = listTenantExportConfigs_listTenantExportConfigs;

type FormValues = {
  bucket: string;
  prefix: string;
  awsRegion: string;
  awsAccessKeyId: string;
  awsSecretAccessKey: string;
  incremental: "" | "true";
};

const AWS_REGION_OPTIONS = [
  { label: "us-east-1 - US East (N. Virginia)", value: "us-east-1" },
  { label: "us-east-2 - US East (Ohio)", value: "us-east-2" },
  { label: "us-west-1 - US West (N. California)", value: "us-west-1" },
  { label: "us-west-2 - US West (Oregon)", value: "us-west-2" },
  { label: "af-south-1 - Africa (Cape Town)", value: "af-south-1" },
  { label: "ap-east-1 - Asia Pacific (Hong Kong)", value: "ap-east-1" },
  { label: "ap-southeast-3 - Asia Pacific (Jakarta)", value: "ap-southeast-3" },
  { label: "ap-south-1 - Asia Pacific (Mumbai)", value: "ap-south-1" },
  { label: "ap-northeast-3 - Asia Pacific (Osaka)", value: "ap-northeast-3" },
  { label: "ap-northeast-2 - Asia Pacific (Seoul)", value: "ap-northeast-2" },
  {
    label: "ap-southeast-1 - Asia Pacific (Singapore)",
    value: "ap-southeast-1"
  },
  { label: "ap-southeast-2 - Asia Pacific (Sydney)", value: "ap-southeast-2" },
  { label: "ap-northeast-1 - Asia Pacific (Tokyo)", value: "ap-northeast-1" },
  { label: "ca-central-1 - Canada (Central)", value: "ca-central-1" },
  { label: "cn-north-1 - China (Beijing)", value: "cn-north-1" },
  { label: "cn-northwest-1 - China (Ningxia)", value: "cn-northwest-1" },
  { label: "eu-central-1 - Europe (Frankfurt)", value: "eu-central-1" },
  { label: "eu-west-1 - Europe (Ireland)", value: "eu-west-1" },
  { label: "eu-west-2 - Europe (London)", value: "eu-west-2" },
  { label: "eu-south-1 - Europe (Milan)", value: "eu-south-1" },
  { label: "eu-west-3 - Europe (Paris)", value: "eu-west-3" },
  { label: "eu-north-1 - Europe (Stockholm)", value: "eu-north-1" },
  { label: "me-south-1 - Middle East (Bahrain)", value: "me-south-1" },
  { label: "sa-east-1 - South America (São Paulo)", value: "sa-east-1" }
];

const useStyles = makeStyles(theme => ({
  button: {
    marginRight: theme.spacing(1)
  },
  buttonsWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between"
  }
}));

function S3ExportConfigDialog({
  onClose,
  exportConfig,
  handleSubmitCreate,
  handleSubmitUpdate
}: {
  onClose: () => void;
  exportConfig: ExportConfig | undefined;
  handleSubmitCreate: (
    settings: TenantExportS3SettingsCreateInput
  ) => Promise<void>;
  handleSubmitUpdate: (
    settings: TenantExportS3SettingsUpdateInput
  ) => Promise<void>;
}) {
  const [saveErrors, setSaveErrors] = useState<string | null>(null);
  const tenant = useTenant()!;

  const isUpdate = !!exportConfig;

  const schema = yup.object().shape({
    bucket: yup.string().required("Required"),
    prefix: yup.string(),
    awsRegion: yup
      .string()
      .required("Required")
      .oneOf(AWS_REGION_OPTIONS.map(o => o.value)),
    awsAccessKeyId: yup.string().required("Required"),
    awsSecretAccessKey: isUpdate
      ? yup.string()
      : yup.string().required("Required"),
    incremental: yup.string()
  });

  const initialValues: FormValues = {
    bucket: exportConfig?.s3Settings?.bucket ?? "",
    prefix: exportConfig?.s3Settings?.prefix ?? "",
    awsRegion: exportConfig?.s3Settings?.awsRegion ?? "us-east-1",
    awsAccessKeyId: exportConfig?.s3Settings?.awsAccessKeyId ?? "",
    awsSecretAccessKey: "",
    incremental: exportConfig?.s3Settings?.incremental ? "true" : ""
  };

  const handleSubmit = async (values: FormValues) => {
    setSaveErrors(null);

    try {
      if (isUpdate) {
        await handleSubmitUpdate({
          ...values,
          incremental: values.incremental === "true",
          awsSecretAccessKey:
            values.awsSecretAccessKey.trim().length > 0
              ? values.awsSecretAccessKey
              : undefined
        });
      } else {
        await handleSubmitCreate({
          ...values,
          incremental: values.incremental === "true"
        });
      }
    } catch (e: any) {
      setSaveErrors(e.message);
    }
  };

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>
        {isUpdate ? "Edit S3 Export Configuration" : "Configure S3 Export"}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <DialogContent>
              <FormikErrorBanner
                additionalErrors={saveErrors}
                noScrollOnError
              />
              <FormikTextField fullWidth name="bucket" label="S3 Bucket" />
              <FormikTextField
                fullWidth
                name="prefix"
                label="S3 Object Prefix (optional)"
              />
              <FormikSelect fullWidth name="awsRegion" label="AWS Region">
                {AWS_REGION_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </FormikSelect>
              <FormikTextField
                fullWidth
                name="awsAccessKeyId"
                label="AWS Access Key ID"
              />
              <FormikTextField
                fullWidth
                name="awsSecretAccessKey"
                type="password"
                label={
                  isUpdate
                    ? "AWS Secret Access Key (leave blank to keep current)"
                    : "AWS Secret Access Key"
                }
              />
              <FormikSelect
                fullWidth
                name="incremental"
                label="Advanced: Incremental Sync"
              >
                <MenuItem value="">
                  Disabled (limit{" "}
                  {tenant.s3NonincrementalRowLimit.toLocaleString()} rows per
                  table)
                </MenuItem>
                <MenuItem value="true">
                  Enabled (no row limit; only rows updated in the past{" "}
                  {tenant.s3IncrementalWindowDays} days will be exported)
                </MenuItem>
              </FormikSelect>
            </DialogContent>
            <DialogActions>
              <Button disabled={isSubmitting} onClick={onClose} color="primary">
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                color="primary"
                variant="contained"
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
}

function S3ExportDeleteButton({
  onDelete
}: {
  onDelete: () => Promise<any>;
}): JSX.Element {
  const [loading, setLoading] = useState(false);
  const confirm = useConfirm();

  return (
    <Button
      disabled={loading}
      color="secondary"
      onClick={async () => {
        try {
          await confirm({
            title: "Disable S3 Export?",
            description:
              "Are you sure you want to disable S3 exports? Any data in S3 will remain there, but no new exports will be run. You'll need to re-enter your AWS credentials to re-enable exports."
          });
        } catch {
          // user cancelled
          return;
        }

        setLoading(true);
        await onDelete();
        setLoading(false);
      }}
    >
      {loading ? "Disabling..." : "Disable"}
    </Button>
  );
}

export default function S3TenantExportConfig({
  exportConfig
}: {
  exportConfig: ExportConfig | undefined;
}): JSX.Element {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);

  const [createExportConfig] = useMutation<
    tenantExportConfigCreateS3,
    tenantExportConfigCreateS3Variables
  >(CREATE_S3_TENANT_EXPORT_CONFIG, {
    refetchQueries: ["listTenantExportConfigs"],
    awaitRefetchQueries: true
  });

  async function handleSubmitCreate(
    settings: TenantExportS3SettingsCreateInput
  ) {
    const { errors } = await createExportConfig({
      variables: { name: "S3", settings }
    });

    if (errors) {
      if (graphqlErrorHasCode("INVALID_TENANT_EXPORT_CONFIG", errors)) {
        throw new Error(getErrorMessage(errors));
      } else {
        console.error(errors);
        throw new Error("Error configuring S3 export");
      }
    } else {
      setDialogOpen(false);
    }
  }

  const [updateExportConfig] = useMutation<
    tenantExportConfigUpdateS3,
    tenantExportConfigUpdateS3Variables
  >(UPDATE_S3_TENANT_EXPORT_CONFIG, {
    refetchQueries: ["listTenantExportConfigs"],
    awaitRefetchQueries: true
  });

  async function handleSubmitUpdate(
    settings: TenantExportS3SettingsUpdateInput
  ) {
    const { errors } = await updateExportConfig({
      variables: { id: exportConfig!.id, name: "S3", settings }
    });

    if (errors) {
      if (graphqlErrorHasCode("INVALID_TENANT_EXPORT_CONFIG", errors)) {
        throw new Error(getErrorMessage(errors));
      } else {
        console.error(errors);
        throw new Error("Error configuring S3 export");
      }
    } else {
      setDialogOpen(false);
    }
  }

  const [deleteExportConfig] = useMutation<
    tenantExportConfigDelete,
    tenantExportConfigDeleteVariables
  >(DELETE_TENANT_EXPORT_CONFIG, {
    refetchQueries: ["listTenantExportConfigs"],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Failed to disable S3 export"
  });

  function renderStatus() {
    if (!exportConfig) {
      return "Not configured";
    }

    if (!exportConfig.lastRunJob) {
      return "Has not run yet";
    }

    const startedAt = new Date(
      exportConfig.lastRunJob.createTime
    ).toLocaleString();

    switch (exportConfig.lastRunJob.status) {
      case JobStatus.CREATED:
      case JobStatus.QUEUED:
      case JobStatus.RUNNING:
        return `Running now (started at ${startedAt})`;
      case JobStatus.DONE:
        return `Last run succeeded (started at ${startedAt})`;
      case JobStatus.FAILED:
        return `Last run failed (started at ${startedAt}): ${exportConfig.lastRunJob.errorMessage}`;
    }
  }

  return (
    <Card>
      <CardHeader title="S3 Export" />
      <CardContent>
        <p>
          S3 Exports allow you to export all of your data to AWS S3. From there,
          you can query the data directly using Amazon Athena, or load it into
          Civis, Amazon Redshift, or other data warehouses. S3 Exports run
          nightly.{" "}
          <a
            href="https://support.scaletowin.com/s3-export"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn More
          </a>
          .
        </p>
        <p>
          <strong>Status:</strong> {renderStatus()}
        </p>
        <div className={classes.buttonsWrapper}>
          <div>
            <Button
              onClick={() => setDialogOpen(true)}
              variant="contained"
              color="primary"
              className={classes.button}
            >
              {exportConfig ? "Edit Configuration" : "Configure"}
            </Button>
            {exportConfig && (
              <S3ExportDeleteButton
                onDelete={() =>
                  deleteExportConfig({ variables: { id: exportConfig.id } })
                }
              />
            )}
          </div>
          <div>
            <Link
              component={Button}
              to="/organization/exports/data-dictionary"
              color="primary"
            >
              Data Dictionary
            </Link>
          </div>
        </div>

        {dialogOpen && (
          <S3ExportConfigDialog
            onClose={() => setDialogOpen(false)}
            exportConfig={exportConfig}
            handleSubmitCreate={handleSubmitCreate}
            handleSubmitUpdate={handleSubmitUpdate}
          />
        )}
      </CardContent>
    </Card>
  );
}
